import { useState, useRef, useEffect } from 'react';
import { BankAccount } from '@budgira/shared';
import { BankIcon } from './BankIcon';

// Icons as inline SVG components
const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const BanknotesIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
  </svg>
);

interface AccountSelectorProps {
  accounts: BankAccount[];
  selectedAccountId: string;
  onAccountChange: (accountId: string) => void;
  className?: string;
}

export function AccountSelector({
  accounts,
  selectedAccountId,
  onAccountChange,
  className = '',
}: AccountSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSelect = (accountId: string) => {
    onAccountChange(accountId);
    setIsOpen(false);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="group flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border border-violet-200 dark:border-violet-700/50 hover:border-violet-400 dark:hover:border-violet-500 hover:shadow-md hover:shadow-violet-100 dark:hover:shadow-violet-900/20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {/* Account Icon with Bank Colors */}
        {selectedAccount ? (
          <BankIcon 
            bankName={selectedAccount.bankName || selectedAccount.name} 
            size="sm"
          />
        ) : (
          <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-sm">
            <BanknotesIcon className="w-4 h-4 text-white" />
          </div>
        )}

        {/* Account Info */}
        <div className="flex flex-col items-start min-w-0">
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate max-w-[180px]">
            {selectedAccount?.name || 'Sélectionner un compte'}
          </span>
          {selectedAccount && (
            <span
              className={`text-xs font-medium ${
                (selectedAccount.balance ?? 0) >= 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              }`}
            >
              {formatCurrency(selectedAccount.balance ?? 0, selectedAccount.currency)}
            </span>
          )}
        </div>

        {/* Chevron */}
        <ChevronDownIcon
          className={`w-4 h-4 text-violet-500 dark:text-violet-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute z-50 mt-2 w-72 rounded-xl bg-white dark:bg-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
          role="listbox"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/30 dark:to-indigo-900/30 border-b border-slate-200 dark:border-slate-700">
            <p className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider">
              Choisir un compte
            </p>
          </div>

          {/* Options */}
          <div className="py-2 max-h-64 overflow-y-auto">
            {accounts.map((account) => {
              const isSelected = account.id === selectedAccountId;
              return (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => handleSelect(account.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-150 ${
                    isSelected
                      ? 'bg-violet-50 dark:bg-violet-900/30'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  }`}
                  role="option"
                  aria-selected={isSelected}
                >
                  {/* Account Icon with Bank Colors */}
                  <BankIcon 
                    bankName={account.bankName || account.name} 
                    size="md"
                  />

                  {/* Account Details */}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium truncate ${
                        isSelected
                          ? 'text-violet-900 dark:text-violet-100'
                          : 'text-slate-900 dark:text-slate-100'
                      }`}
                    >
                      {account.name}
                    </p>
                    <p
                      className={`text-xs font-medium ${
                        (account.balance ?? 0) >= 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {formatCurrency(account.balance ?? 0, account.currency)}
                    </p>
                  </div>

                  {/* Check Icon */}
                  {isSelected && (
                    <CheckIcon className="flex-shrink-0 w-5 h-5 text-violet-600 dark:text-violet-400" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer hint */}
          {accounts.length > 1 && (
            <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                {accounts.length} comptes disponibles
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountSelector;
